import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Trophy, Sparkles, Star, Zap, Headphones } from 'lucide-react'

// ============================================
// CARD DEALING ANIMATION
// Cards fly from dealer position to each player
// ============================================

interface DealCardProps {
  targetX: number
  targetY: number
  delay: number
  onComplete?: () => void
}

export function DealCardAnimation({ targetX, targetY, delay, onComplete }: DealCardProps) {
  return (
    <motion.div
      className="absolute w-10 h-14 z-50 pointer-events-none"
      style={{ top: '50%', left: '50%' }}
      initial={{ x: 0, y: 0, rotate: 0, scale: 0.6, opacity: 0 }}
      animate={{
        x: targetX,
        y: targetY,
        rotate: [0, -15, 5, 0],
        scale: [0.6, 1.1, 1],
        opacity: [0, 1, 1],
      }}
      transition={{
        duration: 0.5,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      onAnimationComplete={onComplete}
    >
      {/* Card back */}
      <div className="w-full h-full rounded-md bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border border-blue-700/50 shadow-lg overflow-hidden">
        <div className="absolute inset-1 rounded border border-blue-600/30">
          <div className="absolute inset-0 bg-[repeating-conic-gradient(#1e3a5f_0%_25%,#1e40af_0%_50%)] opacity-30 bg-[length:8px_8px]" />
        </div>
      </div>
    </motion.div>
  )
}

// ============================================
// CHIP MOVEMENT ANIMATION
// Chips slide from player position to pot center
// ============================================

interface ChipMoveProps {
  fromX: number
  fromY: number
  toX: number
  toY: number
  amount: number
  delay?: number
  color?: string
}

export function ChipMoveAnimation({ fromX, fromY, toX, toY, amount, delay = 0, color = 'gold' }: ChipMoveProps) {
  const chipColors: Record<string, string> = {
    gold: 'from-amber-500 to-amber-600 border-amber-400',
    red: 'from-red-500 to-red-600 border-red-400',
    blue: 'from-blue-500 to-blue-600 border-blue-400',
    green: 'from-emerald-500 to-emerald-600 border-emerald-400',
    black: 'from-gray-700 to-gray-800 border-gray-600',
  }

  return (
    <motion.div
      className="absolute z-40 pointer-events-none flex flex-col items-center"
      initial={{ x: fromX, y: fromY, scale: 0.8, opacity: 0 }}
      animate={{
        x: toX,
        y: toY,
        scale: [0.8, 1.2, 1],
        opacity: [0, 1, 1, 0.8],
      }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Chip stack */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className={cn(
            "w-8 h-2 rounded-full bg-gradient-to-b border shadow-md -mt-0.5 first:mt-0",
            chipColors[color] || chipColors.gold
          )}
          initial={{ y: i * -4 }}
          animate={{ y: 0 }}
          transition={{ delay: delay + i * 0.05 }}
        />
      ))}
      {/* Amount label */}
      <motion.span
        className="text-[10px] font-mono font-bold text-gold mt-1 whitespace-nowrap"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: delay + 0.3 }}
      >
        ${amount}
      </motion.span>
    </motion.div>
  )
}

// ============================================
// POT DISTRIBUTION ANIMATION
// Chips slide from pot center to winner
// ============================================

interface PotDistributeProps {
  winnerX: number
  winnerY: number
  amount: number
  onComplete?: () => void
}

export function PotDistributeAnimation({ winnerX, winnerY, amount, onComplete }: PotDistributeProps) {
  return (
    <motion.div
      className="absolute z-40 pointer-events-none"
      style={{ top: '42%', left: '50%' }}
      initial={{ x: 0, y: 0, scale: 1 }}
      animate={{
        x: winnerX,
        y: winnerY,
        scale: [1, 1.3, 0.8],
        opacity: [1, 1, 0],
      }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      onAnimationComplete={onComplete}
    >
      <div className="flex flex-col items-center">
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className="w-10 h-2.5 rounded-full bg-gradient-to-b from-amber-500 to-amber-600 border border-amber-400 shadow-md -mt-0.5 first:mt-0"
            initial={{ x: (i % 2 === 0 ? -1 : 1) * 4 }}
            animate={{ x: 0 }}
            transition={{ delay: i * 0.03 }}
          />
        ))}
        <span className="text-sm font-mono font-bold text-gold mt-1">+${amount}</span>
      </div>
    </motion.div>
  )
}

// ============================================
// CARD FLIP ANIMATION (Showdown)
// Card flips from back to front
// ============================================

interface CardFlipProps {
  rank: string
  suit: string
  delay?: number
}

const getSuitColor = (suit: string) => suit === 'hearts' || suit === 'diamonds' ? 'text-red-500' : 'text-slate-800'
const getSuitIcon = (suit: string) => {
  switch (suit) {
    case 'spades': return '♠'
    case 'hearts': return '♥'
    case 'clubs': return '♣'
    case 'diamonds': return '♦'
    default: return ''
  }
}

export function CardFlipAnimation({ rank, suit, delay = 0 }: CardFlipProps) {
  const [flipped, setFlipped] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setFlipped(true), (delay + 0.5) * 1000)
    return () => clearTimeout(t)
  }, [delay])

  return (
    <div className="w-14 h-20 perspective-1000" style={{ perspective: '600px' }}>
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Card Back */}
        <div
          className="absolute inset-0 rounded-lg bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border border-blue-700/50 shadow-xl"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="absolute inset-1 rounded border border-blue-600/30" />
        </div>

        {/* Card Front */}
        <div
          className="absolute inset-0 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 shadow-xl overflow-hidden"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className={cn("absolute top-1 left-1.5 flex flex-col items-center leading-none", getSuitColor(suit))}>
            <span className="text-sm font-bold font-serif">{rank}</span>
            <span className="text-xs">{getSuitIcon(suit)}</span>
          </div>
          <div className={cn("absolute inset-0 flex items-center justify-center", getSuitColor(suit))}>
            <span className="text-3xl drop-shadow-sm">{getSuitIcon(suit)}</span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent" />
        </div>
      </motion.div>
    </div>
  )
}

// ============================================
// FOLD ANIMATION
// Cards toss toward center (muck)
// ============================================

export function FoldAnimation({ fromX, fromY }: { fromX: number; fromY: number }) {
  return (
    <>
      {[0, 1].map((i) => (
        <motion.div
          key={i}
          className="absolute w-10 h-14 z-40 pointer-events-none"
          style={{ left: fromX + i * 6, top: fromY }}
          initial={{ rotate: i * 5, opacity: 1, scale: 1 }}
          animate={{
            x: -fromX * 0.4,
            y: -fromY * 0.3,
            rotate: [i * 5, -20 + i * 15, -30],
            opacity: [1, 0.8, 0],
            scale: [1, 0.8, 0.5],
          }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="w-full h-full rounded-md bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border border-blue-700/50 shadow-lg" />
        </motion.div>
      ))}
    </>
  )
}

// ============================================
// WIN CELEBRATION
// Scaled by win size: small, medium, big, jackpot
// ============================================

type CelebrationSize = 'small' | 'medium' | 'big' | 'jackpot'
type CelebrationMode = 'inline' | 'fullscreen'

interface WinCelebrationProps {
  size: CelebrationSize
  amount: number
  playerName: string
  mode?: CelebrationMode
  onComplete?: () => void
}

export function WinCelebration({ size, amount, playerName, mode, onComplete }: WinCelebrationProps) {
  const [visible, setVisible] = useState(true)

  // Auto-resolve mode: inline for small/medium, fullscreen for big/jackpot
  const resolvedMode = mode ?? (size === 'big' || size === 'jackpot' ? 'fullscreen' : 'inline')

  useEffect(() => {
    const duration = size === 'jackpot' ? 5000 : size === 'big' ? 3500 : size === 'medium' ? 2500 : 1500
    const t = setTimeout(() => {
      setVisible(false)
      onComplete?.()
    }, duration)
    return () => clearTimeout(t)
  }, [size, onComplete])

  if (!visible) return null

  // Inline mode — compact badge, no overlay
  if (resolvedMode === 'inline') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none"
      >
        <div className="px-5 py-2 rounded-2xl bg-black/60 backdrop-blur-sm border border-gold/30 text-center shadow-[0_0_20px_rgba(212,175,55,0.3)]">
          <p className="text-[10px] text-muted-foreground">{playerName}</p>
          <p className="text-xl font-mono font-bold text-gradient-gold">+${amount.toLocaleString()}</p>
        </div>
      </motion.div>
    )
  }

  // Medium fullscreen win
  if (size === 'medium') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
      >
        <div className="relative">
          {/* Glow */}
          <div className="absolute inset-0 bg-gold/20 blur-3xl rounded-full scale-150" />
          <div className="relative glass-card-elevated px-12 py-6 rounded-3xl text-center border-gold/30">
            <Trophy className="w-8 h-8 text-gold mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-1">{playerName} 勝出!</p>
            <p className="text-4xl font-mono font-bold text-gradient-gold">+${amount.toLocaleString()}</p>
          </div>
        </div>
      </motion.div>
    )
  }

  // Big win
  if (size === 'big') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
      >
        {/* Background dim */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

        {/* Particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-gold"
            style={{ top: '50%', left: '50%' }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{
              x: (Math.random() - 0.5) * 400,
              y: (Math.random() - 0.5) * 300,
              opacity: [1, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{ duration: 2, delay: Math.random() * 0.5, ease: 'easeOut' }}
          />
        ))}

        <motion.div
          initial={{ scale: 0, rotateZ: -10 }}
          animate={{ scale: [0, 1.2, 1], rotateZ: [-10, 5, 0] }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gold/30 blur-[60px] rounded-full scale-200" />
          <div className="relative glass-card-elevated px-16 py-10 rounded-3xl text-center border-gold/40">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Sparkles className="w-12 h-12 text-gold mx-auto mb-3" />
            </motion.div>
            <p className="text-lg text-foreground font-semibold mb-1">{playerName}</p>
            <p className="text-xs text-gold uppercase tracking-widest mb-3 font-bold">大贏家</p>
            <motion.p
              className="text-5xl font-mono font-bold text-gradient-gold-shimmer"
              initial={{ scale: 0.5 }}
              animate={{ scale: [0.5, 1.1, 1] }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              +${amount.toLocaleString()}
            </motion.p>
          </div>
        </motion.div>
      </motion.div>
    )
  }

  // Jackpot win - maximum celebration
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
    >
      {/* Full screen overlay */}
      <motion.div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      {/* Rays */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        {Array.from({ length: 12 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 bg-gradient-to-t from-gold/0 via-gold/40 to-gold/0"
            style={{
              height: '200%',
              transformOrigin: 'center center',
              rotate: `${i * 30}deg`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.5, 0.3], scale: [0.5, 1.2, 1] }}
            transition={{ duration: 2, delay: i * 0.05, repeat: Infinity, repeatType: 'reverse' }}
          />
        ))}
      </div>

      {/* Sparkle particles */}
      {Array.from({ length: 40 }).map((_, i) => (
        <motion.div
          key={i}
          className={cn(
            "absolute rounded-full",
            i % 3 === 0 ? "w-3 h-3 bg-gold" : i % 3 === 1 ? "w-2 h-2 bg-amber-300" : "w-1.5 h-1.5 bg-yellow-200"
          )}
          style={{ top: '50%', left: '50%' }}
          initial={{ x: 0, y: 0, opacity: 0 }}
          animate={{
            x: (Math.random() - 0.5) * 600,
            y: (Math.random() - 0.5) * 400,
            opacity: [0, 1, 1, 0],
            scale: [0, 1.5, 1, 0],
          }}
          transition={{
            duration: 3,
            delay: Math.random() * 1,
            repeat: Infinity,
            repeatDelay: Math.random() * 2,
          }}
        />
      ))}

      {/* Main content */}
      <motion.div
        initial={{ scale: 0, rotateZ: -15 }}
        animate={{ scale: [0, 1.3, 1], rotateZ: [-15, 5, 0] }}
        transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
        className="relative z-10"
      >
        <div className="absolute inset-0 bg-gold/40 blur-[80px] rounded-full scale-150" />
        <div className="relative p-12 rounded-[2rem] text-center border-2 border-gold/50 bg-gradient-to-b from-[#1a1410]/95 to-[#0a0c10]/95 backdrop-blur-xl">
          <motion.div
            animate={{ y: [0, -8, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Star className="w-16 h-16 text-gold mx-auto mb-4 fill-gold" />
          </motion.div>
          <motion.p
            className="text-2xl font-display font-bold text-gradient-gold mb-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            JACKPOT!
          </motion.p>
          <p className="text-sm text-muted-foreground mb-2">{playerName}</p>
          <p className="text-xs text-gold/60 uppercase tracking-widest font-bold mb-4">累積獎金中獎</p>
          <motion.p
            className="text-6xl font-mono font-bold text-gradient-gold-shimmer"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            +${amount.toLocaleString()}
          </motion.p>
          <motion.div
            className="mt-5 flex flex-col items-center gap-1.5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Headphones className="w-3.5 h-3.5 text-gold/60" />
              <span className="text-xs">請聯繫管理員領取獎金</span>
            </div>
            <span className="text-[10px] text-muted-foreground/50">Contact Admin for Payout</span>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ============================================
// ANIMATION DEMO PANEL
// Trigger all animations for client review
// ============================================

export function AnimationDemoPanel({ onTrigger }: { onTrigger: (type: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -100 }}
      animate={{ opacity: 1, x: 0 }}
      className="absolute top-16 left-4 z-50 glass-card-elevated rounded-2xl p-3 space-y-1.5 w-32"
    >
      <p className="text-[10px] font-bold text-gold uppercase tracking-wider mb-2">動畫展示</p>
      {[
        { id: 'deal', label: '發牌' },
        { id: 'chips', label: '下注' },
        { id: 'pot', label: '分配彩池' },
        { id: 'fold', label: '棄牌' },
        { id: 'showdown', label: '攤牌' },
        { id: 'win-small', label: '小勝' },
        { id: 'win-medium', label: '中勝' },
        { id: 'win-big', label: '大勝' },
        { id: 'jackpot', label: '累積獎金' },
      ].map((item) => (
        <button
          key={item.id}
          onClick={() => onTrigger(item.id)}
          className="w-full text-left px-3 py-2 rounded-lg text-[11px] font-semibold text-foreground hover:bg-gold/10 hover:text-gold transition-all flex items-center gap-2"
        >
          <Zap className="w-3 h-3 text-gold/50" />
          {item.label}
        </button>
      ))}
    </motion.div>
  )
}
